import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', component: () => import('@/views/LoginView.vue'), meta: { public: true } },
    // MCP connector consent (docs/mcp.md). Signed-in only; not under /mcp, which nginx sends to the API.
    { path: '/connect', component: () => import('@/views/ConnectView.vue') },
    {
      path: '/register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      // From the emailed link. Works signed in or out: the link may be opened
      // on a device that is still signed in.
      path: '/reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { open: true },
    },
    {
      // Arriving from sign-up on klunkers.bike, with a handoff token in the
      // URL fragment. Open: a different account may be signed in here.
      path: '/welcome',
      component: () => import('@/views/WelcomeView.vue'),
      meta: { open: true },
    },
    {
      // Opened from Health Connect's permission screen; readable signed out.
      path: '/privacy',
      component: () => import('@/views/HealthDataView.vue'),
      meta: { open: true },
    },
    {
      // Device check (platform, local store, API). Reachable signed in or out.
      path: '/diagnostics',
      component: () => import('@/views/DiagnosticsView.vue'),
      meta: { open: true },
    },
    {
      // Full-screen, no navigation: the job on the bench.
      path: '/workshop/:id',
      component: () => import('@/views/WorkshopView.vue'),
      props: true,
    },
    {
      path: '/',
      component: () => import('@/layouts/AppShell.vue'),
      children: [
        { path: '', component: () => import('@/views/GarageView.vue') },
        { path: 'bikes/new', component: () => import('@/views/BikeNewView.vue') },
        { path: 'bikes/:id', component: () => import('@/views/BikeView.vue'), props: true },
        {
          path: 'bikes/:id/edit',
          component: () => import('@/views/BikeEditView.vue'),
          props: true,
        },
        {
          path: 'bikes/:id/parts/new',
          component: () => import('@/views/PartNewView.vue'),
          props: true,
        },
        { path: 'parts/:id', component: () => import('@/views/PartView.vue'), props: true },
        {
          path: 'parts/:id/edit',
          component: () => import('@/views/PartEditView.vue'),
          props: true,
        },
        // The Garage page on its Spares tab.
        { path: 'spares', component: () => import('@/views/GarageView.vue') },
        { path: 'spares/new', component: () => import('@/views/PartNewView.vue') },
        { path: 'due', component: () => import('@/views/DueView.vue') },
        { path: 'inbox', component: () => import('@/views/InboxView.vue') },
        { path: 'jobs', component: () => import('@/views/JobsView.vue') },
        { path: 'jobs/:id', component: () => import('@/views/JobView.vue'), props: true },
        {
          path: 'bikes/:id/check',
          component: () => import('@/views/PreRideCheckView.vue'),
          props: true,
        },
        { path: 'checklists', component: () => import('@/views/ChecklistsView.vue') },
        { path: 'shopping-list', component: () => import('@/views/ShoppingListView.vue') },
        { path: 'settings', component: () => import('@/views/SettingsView.vue') },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

/** Signed-out users go to /login (remembering where they were heading). */
export async function authGuard(to: RouteLocationNormalized) {
  const auth = useAuthStore()
  await auth.init()
  if (to.meta.open) return true
  if (to.meta.public) return auth.isSignedIn ? '/' : true
  if (!auth.isSignedIn)
    return { path: '/login', query: to.fullPath === '/' ? {} : { next: to.fullPath } }
  return true
}

router.beforeEach(authGuard)
